import { createRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";

import { ProtectedRoute } from "../auth/ProtectedRoute";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { cn } from "../lib/utils";
import {
  useAutomationActions,
  useAutomationHistory,
  useAutomations,
  type Automation,
  type AutomationStatus,
} from "../queries";
import { rootRoute } from "./__root";

export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: "/settings/automations",
  component: AutomationsRoute,
});

type AutomationKind = "Customer outreach" | "Recovery paused" | "Sync and monitoring" | "Internal";

const AUTOMATION_META: Record<
  string,
  { description: string; kind: AutomationKind; resumeLocked?: boolean; ownerNote?: string }
> = {
  "relance-live": {
    description: "Regular quote follow-up sequence.",
    kind: "Customer outreach",
  },
  "review-campaign": {
    description: "First review request after completed work.",
    kind: "Customer outreach",
  },
  "review-reminders": {
    description: "Follow-up reminders for review requests.",
    kind: "Customer outreach",
  },
  "referral-campaign": {
    description: "Referral request campaign.",
    kind: "Customer outreach",
  },
  "care-guide-delivery": {
    description: "Electronic care guide delivery.",
    kind: "Customer outreach",
  },
  "older-first-touch": {
    description: "Old-contact reactivation. Keep paused until the suppression list is rebuilt.",
    kind: "Recovery paused",
    ownerNote: "Paused intentionally after opt-out risk.",
  },
  "wfy-recovery": {
    description: "WebFindYou abandoned-lead recovery.",
    kind: "Recovery paused",
    resumeLocked: true,
    ownerNote: "Resume is locked until Marc approves a fresh WFY policy.",
  },
  "wfy-reply-matcher": {
    description: "Matches inbound replies. Does not send customer outreach.",
    kind: "Sync and monitoring",
  },
  "public-reviews-sync": {
    description: "Reads public review data.",
    kind: "Sync and monitoring",
  },
  "email-shadow": {
    description: "Inbox monitoring for customer replies.",
    kind: "Sync and monitoring",
  },
  "gmail-sent-sync": {
    description: "Syncs sent Gmail records for audit and suppression.",
    kind: "Sync and monitoring",
  },
  "twilio-sms-sync": {
    description: "Syncs SMS records.",
    kind: "Sync and monitoring",
  },
  "control-charts": {
    description: "KPI control chart computation.",
    kind: "Internal",
  },
  "antonio-daily": {
    description: "Retired daily brief. Use Today in the dashboard instead.",
    kind: "Internal",
    resumeLocked: true,
    ownerNote: "Disabled intentionally after Today became the priority queue.",
  },
};

const KIND_ORDER: AutomationKind[] = [
  "Customer outreach",
  "Recovery paused",
  "Sync and monitoring",
  "Internal",
];

function AutomationsRoute() {
  return (
    <ProtectedRoute requiredRoles={["owner", "gm"]}>
      <AutomationsContent />
    </ProtectedRoute>
  );
}

function AutomationsContent() {
  const automations = useAutomations();
  const history = useAutomationHistory(12);
  const actions = useAutomationActions();
  const [confirm, setConfirm] = useState<{ action: "pause" | "resume"; automation: Automation } | null>(null);

  const groups = useMemo(() => {
    const rows = automations.data?.automations || [];
    return KIND_ORDER.map((kind) => ({
      kind,
      rows: rows.filter((row) => getMeta(row.unit).kind === kind),
    })).filter((group) => group.rows.length > 0);
  }, [automations.data]);

  const busyUnit =
    actions.pause.variables ||
    actions.resume.variables ||
    actions.trigger.variables ||
    null;
  const actionError = actions.pause.error || actions.resume.error || actions.trigger.error;

  async function runConfirmed() {
    if (!confirm) return;
    const unit = confirm.automation.unit;
    if (confirm.action === "pause") {
      await actions.pause.mutateAsync(unit);
    } else {
      await actions.resume.mutateAsync(unit);
    }
    setConfirm(null);
  }

  return (
    <section className="space-y-5">
      <div>
        <Badge variant="slate">Admin</Badge>
        <h1 className="mt-3 text-2xl font-semibold text-slate-950">Automation Control</h1>
        <p className="mt-1 max-w-3xl text-sm text-slate-600">
          Owner switchboard for live automations. Green means the timer is enabled and healthy.
          Red means paused, failed, or unavailable.
        </p>
      </div>

      <Card>
        <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <CardTitle>Live status</CardTitle>
            <p className="mt-1 text-xs text-slate-500">
              Status is read from systemd on the VPS. Pause and resume actions are audited.
            </p>
          </div>
          <Button variant="secondary" onClick={() => void automations.refetch()} disabled={automations.isFetching}>
            Refresh
          </Button>
        </CardHeader>
        <CardContent className="space-y-5">
          {automations.isLoading ? <p className="text-sm text-slate-500">Loading automations...</p> : null}
          {automations.error ? (
            <p className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              {automations.error instanceof Error ? automations.error.message : "Automation status failed"}
            </p>
          ) : null}
          {actionError ? (
            <p className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              {actionError instanceof Error ? actionError.message : "Automation action failed"}
            </p>
          ) : null}

          {groups.map((group) => (
            <div key={group.kind} className="space-y-2">
              <div className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">
                {group.kind}
              </div>
              <div className="overflow-hidden rounded-md border border-slate-200 bg-white">
                <table className="w-full min-w-[760px] text-left text-sm">
                  <thead className="bg-slate-50 text-xs font-semibold uppercase tracking-[0.08em] text-slate-500">
                    <tr>
                      <th className="w-16 px-3 py-2">LED</th>
                      <th className="px-3 py-2">Automation</th>
                      <th className="px-3 py-2">Status</th>
                      <th className="px-3 py-2">Last run</th>
                      <th className="px-3 py-2">Next run</th>
                      <th className="px-3 py-2 text-right">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {group.rows.map((automation) => {
                      const meta = getMeta(automation.unit);
                      const isRunning = isHealthy(automation.status);
                      const locked = meta.resumeLocked && !isRunning;
                      const isBusy = busyUnit === automation.unit;
                      return (
                        <tr key={automation.unit} className="border-t border-slate-100 align-top">
                          <td className="px-3 py-3">
                            <StatusLed status={automation.status} />
                          </td>
                          <td className="px-3 py-3">
                            <div className="font-medium text-slate-950">
                              {automation.label || automation.unit}
                            </div>
                            <div className="mt-1 text-xs leading-relaxed text-slate-500">
                              {meta.description}
                            </div>
                            {meta.ownerNote ? (
                              <div className="mt-1 text-xs font-medium text-amber-700">
                                {meta.ownerNote}
                              </div>
                            ) : null}
                            <div className="mt-1 font-mono text-[11px] text-slate-400">
                              {automation.unit}.timer
                            </div>
                          </td>
                          <td className="px-3 py-3">
                            <StatusBadge status={automation.status} enabled={automation.enabled} />
                            <div className="mt-1 text-xs text-slate-500">
                              {automation.result || automation.activeState || "no result"}
                            </div>
                          </td>
                          <td className="px-3 py-3 text-xs text-slate-600">{formatDateTime(automation.lastRun)}</td>
                          <td className="px-3 py-3 text-xs text-slate-600">{formatDateTime(automation.nextRun)}</td>
                          <td className="px-3 py-3 text-right">
                            {isRunning ? (
                              <Button
                                variant="secondary"
                                className="border-red-200 text-red-700 hover:bg-red-50"
                                disabled={isBusy}
                                onClick={() => setConfirm({ action: "pause", automation })}
                              >
                                Pause
                              </Button>
                            ) : (
                              <Button
                                className={cn(locked ? "bg-slate-300 text-slate-600 hover:bg-slate-300" : "bg-emerald-600 hover:bg-emerald-700")}
                                disabled={isBusy || locked}
                                onClick={() => setConfirm({ action: "resume", automation })}
                              >
                                {locked ? "Locked" : "Resume"}
                              </Button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Recent automation actions</CardTitle>
        </CardHeader>
        <CardContent>
          {history.isLoading ? <p className="text-sm text-slate-500">Loading audit history...</p> : null}
          {history.error ? (
            <p className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              {history.error instanceof Error ? history.error.message : "Audit history failed"}
            </p>
          ) : null}
          <div className="overflow-hidden rounded-md border border-slate-200">
            <table className="w-full min-w-[680px] text-left text-sm">
              <thead className="bg-slate-50 text-xs font-semibold uppercase tracking-[0.08em] text-slate-500">
                <tr>
                  <th className="px-3 py-2">Time</th>
                  <th className="px-3 py-2">Action</th>
                  <th className="px-3 py-2">Target</th>
                  <th className="px-3 py-2">Actor</th>
                  <th className="px-3 py-2">Result</th>
                </tr>
              </thead>
              <tbody>
                {(history.data?.actions || []).map((row) => (
                  <tr key={row.id} className="border-t border-slate-100">
                    <td className="px-3 py-2 text-xs text-slate-600">{formatDateTime(row.ts_utc)}</td>
                    <td className="px-3 py-2 font-medium text-slate-950">{row.action}</td>
                    <td className="px-3 py-2 font-mono text-xs text-slate-600">{row.target}</td>
                    <td className="px-3 py-2 text-xs text-slate-600">{row.actor}</td>
                    <td className="px-3 py-2">
                      <Badge variant={row.status === "success" ? "green" : row.status === "failed" ? "red" : "slate"}>
                        {row.status}
                      </Badge>
                    </td>
                  </tr>
                ))}
                {!history.isLoading && !history.data?.actions.length ? (
                  <tr>
                    <td colSpan={5} className="px-3 py-6 text-center text-sm text-slate-500">
                      No automation actions yet.
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {confirm ? (
        <ConfirmDialog
          action={confirm.action}
          automation={confirm.automation}
          busy={actions.pause.isPending || actions.resume.isPending}
          onCancel={() => setConfirm(null)}
          onConfirm={() => void runConfirmed()}
        />
      ) : null}
    </section>
  );
}

function getMeta(unit: string) {
  return AUTOMATION_META[unit] || { description: "System automation.", kind: "Internal" as AutomationKind };
}

function isHealthy(status: AutomationStatus) {
  return status === "ok" || status === "active";
}

function StatusLed({ status }: { status: AutomationStatus }) {
  const healthy = isHealthy(status);
  return (
    <span
      className={cn(
        "inline-flex h-4 w-4 rounded-full border shadow-[0_0_0_4px_rgba(148,163,184,0.12)]",
        healthy ? "border-emerald-300 bg-emerald-500" : "border-red-300 bg-red-500",
      )}
      title={healthy ? "Functional" : "Not functional"}
    />
  );
}

function StatusBadge({ status, enabled }: { status: AutomationStatus; enabled: boolean }) {
  const healthy = isHealthy(status);
  return (
    <Badge variant={healthy ? "green" : "red"}>
      {healthy ? "Running" : status === "paused" ? "Paused" : status}
      {enabled ? "" : " / off"}
    </Badge>
  );
}

function formatDateTime(value: string | null) {
  if (!value) return "n/a";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "n/a";
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

function ConfirmDialog({
  action,
  automation,
  busy,
  onCancel,
  onConfirm,
}: {
  action: "pause" | "resume";
  automation: Automation;
  busy: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const isPause = action === "pause";
  return (
    <div className="mobile-dialog-backdrop">
      <div className="mobile-dialog-panel max-w-md p-5">
        <h2 className="text-base font-semibold text-slate-950">
          {isPause ? "Pause automation?" : "Resume automation?"}
        </h2>
        <p className="mt-2 text-sm leading-relaxed text-slate-600">
          {isPause ? "This will stop future scheduled runs for" : "This will turn the scheduled timer back on for"}{" "}
          <span className="font-semibold text-slate-950">{automation.label || automation.unit}</span>.
        </p>
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="secondary" onClick={onCancel} disabled={busy}>
            Cancel
          </Button>
          <Button
            className={isPause ? "bg-red-600 hover:bg-red-700" : "bg-emerald-600 hover:bg-emerald-700"}
            onClick={onConfirm}
            disabled={busy}
          >
            {busy ? "Working..." : isPause ? "Pause" : "Resume"}
          </Button>
        </div>
      </div>
    </div>
  );
}
